// ----------------------------------------------------------------------------
// Product Form Web Component: iamQuantityInput <iam-quantity-input>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// Quantity selector input for the product form.
// - Allows the user to increment or decrement the quantity value
// - Allows the user to manually enter a quantity value
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:quantity:changed' → Listens for when the quantity is changed,
//                                    but does nothing by default
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Updates the quantity value when the up or down buttons are interacted with
//   or the user enters a value manually
// ----------------------------------------------------------------------------
// Public Methods:
// - `setValue(quantity: number)` → Set the quantity to a specific value
// - `getValue()`                 → Get the current quantity value
// ----------------------------------------------------------------------------
// Gotchas:
// - If an external component wants to modify the quantity, leverage the `setValue`
//   method to ensure the appropriate change events are triggered.
// - Instead of directly modifying the quantity, you likely want to register
//   form validation rules to prevent invalid quantities (that the user can
//   see feedback regarding and resolve manually)
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';

// Validation Extensions (in priority order)
import './quantity-input--variant-exists'; // Validate that the variant actually exists
import './quantity-input--inventory-limits'; // Validate qty against available inventory
import './quantity-input--available'; // Validate that the variant is available for sale
// import './quantity-input--metafield-limits'; // Validate qty against product/variant metafield limits
// import './quantity-input--b2b-limits';       // Validate qty against Shopify B2B limits
import './quantity-input--hardcoded-limits'; // Validate qty against hardcoded limits

// Style includes
import './_quantity-input.scss';

// -------------------------------------
// Functionality
// -------------------------------------

class iamQuantity extends iamFormElement {
  /**
   * Connected Callback
   */
  connectedCallback() {
    this.variantControllerSelector = this.dataset.variantController;
    this.variantController = this.closest(this.variantControllerSelector);
    this.errorContainer = this.variantController.querySelector('.form__errors-container');
    super.connectedCallback();
  }

  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    // Validation
    if (!this.validateRequiredAttributes(['variantControllerSelector', 'variantController', 'errorContainer'])) return;

    // Event listeners for buttons
    this.querySelectorAll('.quantity-input__button').forEach(button => {
      button.addEventListener('click', this.onButtonClick.bind(this));
      button.addEventListener('keypress', this.onButtonPress.bind(this));
    });
  }

  /**
   * Handles the button click event and updates the quantity.
   *
   * @param {Event} event - The click event.
   */
  onButtonClick(event) {
    this.onStepArrow(event);
  }

  /**
   * Handles the button press event and updates the quantity if 'Enter' is pressed.
   *
   * @param {KeyboardEvent} event - The key press event.
   */
  onButtonPress(event) {
    if (event.key === 'Enter') {
      this.onStepArrow(event);
    }
  }

  /**
   * Updates the quantity value and triggers the change event.
   * It applies filters to allow modification of the quantity value before setting it.
   *
   * @param {Event} event - The event triggering the quantity update.
   */
  onStepArrow(event) {
    // Prevent modification if the input is disabled
    if (this.control.disabled) return;

    // Update the quantity based on the button clicked
    if (event.target.classList.contains('quantity-input__button--down')) {
      this.control.stepDown();
    } else {
      this.control.stepUp(); // Default to increment
    }
    this.triggerChange();
  }

  /**
   * Set the value to a specific quantity.
   *
   * @param {number} quantity - The quantity to set
   */
  setValue(quantity) {
    this.control.value = quantity;
    this.triggerChange();
  }

  /**
   * Get the current quantity value
   */
  getValue() {
    return parseInt(this.control.value, 10);
  }

  /**
   * Trigger change event if the input was manipulated programmatically.
   */
  triggerChange() {
    this.control.dispatchEvent(new Event('input', {
      bubbles: true,
      // Allow the event to bubble up the DOM
      cancelable: true,
      // Allow the event to be canceled
      composed: true // Allow event to traverse shadow DOM
    }));
  }

  /**
   * Get the associated item data
   */
  getItemData() {
    var _this$variantControll;
    return ((_this$variantControll = this.variantController) === null || _this$variantControll === void 0 ? void 0 : _this$variantControll.getCurrentData()) || {};
  }
}

// Define the custom element
customElements.define('iam-quantity-input', iamQuantity);